import { Page } from '@/components'
import { useRedNavigate } from '@/dataImplementation/useRedNavigate'
import { EXTERNAL_PATHS, PATHS } from '@/router/paths'
import { Accordion, config, H1, H2, Separator, Spinner, XStack, YStack, Icon, Text } from '@red-ui/components'
import { getString } from '@vrw/data'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FlightDataCard } from '../components/FlightDataCard'
import { FlightSummary } from '../components/FlightSummary'
import { SearchSideSheet } from '../components/SearchSideSheet'
import { TextButton } from '../components/TextButton'
import { SideSheet } from '../components/SideSheet/SideSheet'
import { useVaaAuth } from '../hooks/useVaaAuth'
import { useFlightsSearchParams } from '../hooks/useFlightsSearchParams'
import { useQuery } from '@tanstack/react-query'
import { getNextSearchFlights, getSearchFlights } from '../api/searchFlights'
import { Alert } from '../components/Alert'
import { useSearchSummaryTexts } from '../hooks/useSearchSummaryTexts'
import { Banner } from '../components/Banner'
import { PUBLIC_URL } from '@/config'
import { Link } from '@/components/Link'

export function SearchResults() {
  const [activeItem, setActiveItem] = useState('')
  const location = useLocation()
  const navigate = useRedNavigate()
  const { isLoading, error, data } = useFlightsSearch()
  const { searchParams, setSearchParams } = useFlightsSearchParams()
  const { title, subTitle } = useSearchSummaryTexts({ secondLeg: !!searchParams?.sliceId })

  const onCloseSummarySideSheet = () => {
    setSearchParams((prev) => {
      prev.delete('offerId')
      return prev
    })
  }

  const onContinue = () => {
    setActiveItem('')
    onCloseSummarySideSheet()
  }

  const handleSearchSideSheet = (isOpen: boolean) => () => {
    navigate(`${isOpen ? PATHS.REDSKY_SEARCH_RESULTS_EDIT : PATHS.REDSKY_SEARCH_RESULTS}${location.search}`)
  }

  if (!searchParams) {
    navigate(PATHS.REDSKY_SEARCH)
    return null
  }

  let content = null

  if (isLoading) {
    return (
      <YStack height="100vh" justifyContent="center" alignItems="center" gap="$2">
        <Icon.VirginRed color="$red6" size="$10" />
        <Spinner color="$red6" />
        <H1 textAlign="center">{getString('redSkySearch.results.loading.title')}</H1>
        <Text textAlign="center">{getString('redSkySearch.results.loading.body')}</Text>
      </YStack>
    )
  }

  if (error?.message.includes('"code":"422"') || (!isLoading && !error && data?.flightsAndFares?.length === 0)) {
    content = (
      <Alert
        boxStyling={{ backgroundColor: '$pink10' }}
        title={getString('redSkySearch.results.error.noFlights.title')}
        body={getString('redSkySearch.results.error.noFlights.body')}
        icon={<Icon.AlertCircle color="$pink10" fill="#B3261E" alignSelf="center" minWidth="$3" />}
      />
    )
  } else if (error) {
    content = (
      <Alert
        boxStyling={{ backgroundColor: '$pink10' }}
        title={getString('redSkySearch.results.error.title')}
        body={error.message}
        icon={<Icon.AlertCircle color="$pink10" fill="#B3261E" alignSelf="center" minWidth="$3" />}
      />
    )
  } else {
    content = (
      <YStack flex={1}>
        <XStack marginBottom="$3">
          <Link
            href={EXTERNAL_PATHS.BUY_POINTS}
            aria-label={getString('redSkySearch.results.banner.buyPoints.title')}
            external
            target="_blank"
            hoverStyle={{ textDecorationLine: 'none' }}
            $gtMobile={{ marginRight: '$3' }}>
            <Banner width={405} height={229} imageUrl={`${PUBLIC_URL}/img/red-sky/buy-points-banner.png`}>
              <YStack flex={1}>
                <Text color="$white" fontWeight="600" fontSize="$9" marginBottom="$1">
                  {getString('redSkySearch.results.banner.buyPoints.title')}
                </Text>
                <Text color="$white" fontWeight="400" fontSize="$4">
                  {getString('redSkySearch.results.banner.buyPoints.body')}
                </Text>
              </YStack>
            </Banner>
          </Link>
        </XStack>

        <Accordion value={activeItem} onValueChange={setActiveItem} type="single" collapsible gap="$3" flex={1}>
          {data?.flightsAndFares?.map((flightAndFares) => {
            const key = `${flightAndFares.flight.departure}_${flightAndFares.flight.arrival}`

            return (
              <FlightDataCard
                key={key}
                value={key}
                isActive={key === activeItem}
                onSelect={(id) => {
                  if (searchParams?.form?.date?.to && !searchParams?.sliceId) {
                    setSearchParams((prev) => {
                      prev.set('sliceId', id)
                      prev.set('basketId', data?.basketId)
                      return prev
                    })
                  } else {
                    setSearchParams((prev) => {
                      prev.set('offerId', id)
                      return prev
                    })
                  }
                }}
                data={flightAndFares}
              />
            )
          })}
        </Accordion>
      </YStack>
    )
  }

  return (
    <Page title={getString('redSkySearch.results.title')}>
      <SearchSideSheet
        mainSheetTitle={getString('redSkySearch.results.sidesheet.summary.editSearch')}
        editMode
        initData={searchParams.form}
      />
      <SideSheet.Root
        aria-label={getString('redSkySearch.results.sidesheet.summary.ariaLabel')}
        open={!!searchParams?.offerId}
        onClose={onCloseSummarySideSheet}>
        <SideSheet.Header>{getString('redSkySearch.results.sidesheet.summary')}</SideSheet.Header>
        <FlightSummary
          basketId={data?.basketId}
          onContinue={onContinue}
          onClose={onCloseSummarySideSheet}
          onEditSearch={handleSearchSideSheet(true)}
        />
      </SideSheet.Root>
      <YStack
        gap="$3"
        paddingHorizontal="$2"
        paddingBottom="$3"
        marginHorizontal="auto"
        maxWidth={config.media.desktop.maxWidth}
        $gtMobile={{ paddingHorizontal: '$3' }}
        $gtTabletLandscape={{ paddingHorizontal: '$11' }}>
        <YStack
          // not supported in react-native-web
          style={{ position: 'sticky' }}
          top={0}
          backgroundColor="$white"
          paddingTop="$3"
          zIndex="$5"
          $gtMobile={{ paddingTop: '$4' }}
          $gtTabletLandscape={{ paddingTop: '$6', gap: '$2' }}
          gap="$1">
          <H1 fontSize="$8" $mobile={{ lineHeight: '$7' }} $gtTabletLandscape={{ fontSize: '$9', lineHeight: '$5' }} fontWeight="600">
            {title}
          </H1>
          <XStack
            justifyContent="space-between"
            alignItems="center"
            $mobile={{ flexDirection: 'column', alignItems: 'flex-start' }}
            marginBottom="$1">
            <H2 fontSize="$4" $gtMobile={{ fontSize: '$5' }}>
              {subTitle}
            </H2>
            <TextButton
              $mobile={{ fontSize: '$4' }}
              $gtTabletLandscape={{ fontSize: '$6' }}
              fontSize="$5"
              onPress={handleSearchSideSheet(true)}>
              {getString('redSkySearch.results.search.summary.edit')}
            </TextButton>
          </XStack>
          <Separator />
        </YStack>
        {content}
      </YStack>
    </Page>
  )
}

function useFlightsSearch() {
  const { isLoading: isLoadingVaaAuthData, data: vaaAuthData, error: vaaError } = useVaaAuth()
  const { searchParams } = useFlightsSearchParams()

  const { isLoading, data, error } = useQuery({
    queryKey: ['redsky-search-results', vaaAuthData?.idToken, searchParams?.form, searchParams?.sliceId, searchParams?.basketId],
    retry: false,
    queryFn: async () => {
      if (!searchParams?.form || !vaaAuthData?.idToken) return null
      if (searchParams.sliceId && searchParams.basketId) {
        return getNextSearchFlights({ basketId: searchParams.basketId, offerId: searchParams.sliceId })
      } else {
        return getSearchFlights(searchParams.form)
      }
    },
  })

  return {
    isLoading: isLoadingVaaAuthData || isLoading, // NOSONAR
    error: vaaError || error, // NOSONAR
    data: data
      ? {
          ...data,
          // we don't show non-direct flights in the results
          // we show only virgin atlantic flights in the results
          flightsAndFares: data.flightsAndFares.filter(
            ({ flight: { segments } = {} }) => segments?.length === 1 && segments[0]?.airline?.code === 'VS'
          ),
        }
      : null,
  }
}
